import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { useTenantStore } from '../../../shared/stores/useTenantStore';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
} from '../../charter/components/CharterPage';
import { useCharterServices } from '../../charter/hooks/useCharter';
import {
  useCreatePortfolioWork,
  useDeletePortfolioWork,
  usePortfolio,
  useUpdatePortfolioWork,
} from '../hooks/usePortfolio';
import { useTaggableVariants } from '../hooks/useTaggableVariants';
import { PortfolioWorkForm } from '../components/PortfolioWorkForm';
import { isPortfolioSolution } from '../types';
import type { CreatePortfolioWorkPayload, PortfolioWork } from '../types';

/**
 * Shared Portfolio admin page (PE-1831 follow-up) — consolidates what was charter-only
 * (`CharterPortfolioPage`) into ONE page that works for all three solutions that ship a
 * portfolio-service-backed gallery. The resolved solution drives which backend the hooks
 * below talk to (see api/portfolioApi.ts) and which tagging picker is shown.
 */
export function PortfolioPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const activeMerchant = useTenantStore((s) => s.activeMerchant);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.PORTFOLIO_MANAGE);

  const rawSolutionId = activeMerchant?.solutionId ?? null;
  const solutionId = isPortfolioSolution(rawSolutionId) ? rawSolutionId : null;
  const isCharter = solutionId === 'charter';

  const { data: works = [], isLoading, error } = usePortfolio(selectedTenantId, solutionId);
  const { data: services = [] } = useCharterServices(isCharter ? selectedTenantId : null);
  const { data: variantOptions = [], isLoading: variantsLoading } = useTaggableVariants(
    selectedTenantId,
    !!solutionId && !isCharter,
  );
  const createWork = useCreatePortfolioWork(solutionId);
  const updateWork = useUpdatePortfolioWork(solutionId);
  const deleteWork = useDeletePortfolioWork(solutionId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PortfolioWork | null>(null);

  const isSaving = createWork.isPending || updateWork.isPending;

  const submit = async (values: CreatePortfolioWorkPayload) => {
    if (editing) await updateWork.mutateAsync({ id: editing.id, data: values });
    else await createWork.mutateAsync(values);
    setModalOpen(false);
    setEditing(null);
  };

  const tagOptions = isCharter ? services.map((s) => ({ value: s.id, label: s.name })) : variantOptions;

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Portfolio"
        subtitle="Showcase past work. Customers can book, order or buy &ldquo;from&rdquo; a piece, which then appears as inspiration on the request."
        action={
          canManage && selectedTenantId && solutionId ? (
            <Button
              size="sm"
              className="h-[40px]"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New work
            </Button>
          ) : undefined
        }
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their portfolio" />
      ) : !solutionId ? (
        <ErrorState label="This merchant's solution does not support a portfolio." />
      ) : isLoading ? (
        <LoadingState label="Loading portfolio…" />
      ) : error ? (
        <ErrorState label="Failed to load portfolio." />
      ) : works.length === 0 ? (
        <EmptyState label={`No portfolio works yet.${canManage ? ' Click "New work" to add one.' : ''}`} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {works.map((w) => (
            <div key={w.id} className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="aspect-video bg-muted overflow-hidden">
                {w.media[0] ? (
                  <img src={w.media[0].cdnUrl} alt={w.title} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">No media</div>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-foreground">{w.title}</p>
                {w.caption && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{w.caption}</p>}
                {canManage && (
                  <div className="flex gap-4 mt-3">
                    <button
                      onClick={() => {
                        setEditing(w);
                        setModalOpen(true);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${w.title}"?`)) deleteWork.mutate(w.id);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {solutionId && (
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
          <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit work' : 'New portfolio work'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
                &times;
              </button>
            </div>
            <PortfolioWorkForm
              solutionId={solutionId}
              initial={editing}
              tagOptions={tagOptions}
              tagOptionsLoading={!isCharter && variantsLoading}
              onSubmit={submit}
              onCancel={() => setModalOpen(false)}
              isSaving={isSaving}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
